/**
 * Restrained scroll behaviors (see frontend.md → Motion):
 * 1. Reveals — .reveal elements fade-rise in the first time they
 *    enter the viewport. CSS handles prefers-reduced-motion.
 * 2. Decorative hero video pauses for reduced-motion visitors.
 * 3. Parallax on the project opener image.
 * 4. Transparent nav while over the project opener.
 * (The homepage nav pin is pure CSS — flex order + position: sticky.)
 */

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

function bindHero(scope) {
  const hero = scope.querySelector('.video-hero');
  if (!hero) return;
  // Clicking the hero advances the page so the nav lands pinned at the
  // top (through Lenis when the inertia scroll is active).
  hero.addEventListener('click', () => {
    if (window.__lenis) {
      window.__lenis.scrollTo(hero.offsetHeight);
    } else {
      window.scrollTo({ top: hero.offsetHeight, behavior: prefersReduced.matches ? 'auto' : 'smooth' });
    }
  });
  /**
   * Autoplay hardening: the attributes alone fail for some visitors —
   * Safari can ignore the muted ATTRIBUTE unless the property is set
   * before play(), and iOS Low Power Mode / data-saver block autoplay
   * until the first user gesture. Set the property, call play()
   * (swallowing the rejection), and retry once the video has data, on
   * the first gesture of any kind, and when the tab becomes visible.
   * Per client (2026-08-22) the hero video plays even for
   * reduced-motion visitors — the rest of the site's motion still
   * honors the preference.
   */
  const video = hero.querySelector('.video-hero__media');
  if (!video) return;
  const tryPlay = () => {
    video.muted = true;
    const attempt = video.play();
    if (attempt) attempt.catch(() => {});
  };
  tryPlay();
  video.addEventListener('loadeddata', tryPlay, { once: true });
  const onFirstGesture = () => {
    if (video.paused) tryPlay();
  };
  for (const evt of ['touchstart', 'pointerdown', 'scroll', 'keydown']) {
    window.addEventListener(evt, onFirstGesture, { once: true, passive: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && video.paused) tryPlay();
  });
}

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('reveal--visible');
        revealObserver.unobserve(entry.target);
      }
    }
  },
  { rootMargin: '0px 0px -10% 0px' }
);

/**
 * Every content image gets the same fade-rise entrance as the homepage
 * cards. Sections wrap their imagery in .reveal themselves; this
 * catches the rest — editor-composed blocks, admin rich-text content,
 * skeleton templates — so images animate in consistently site-wide.
 * (Logos, the fixed hero layers, and anything already inside a .reveal
 * are left alone.)
 */
function autoRevealImages(scope) {
  scope.querySelectorAll('img').forEach((img) => {
    if (!img.closest('.shopify-section')) return;
    if (img.closest('.reveal, header, footer, .video-hero, .project-hero-open')) return;
    img.classList.add('reveal');
    revealObserver.observe(img);
  });
}

/**
 * 3. Parallax — the project opener's image drifts slower than the
 *    page. The CSS oversizes it (126% tall) and centers it, so the
 *    rest crop shows the asset's center and a hidden slice exists
 *    above and below; scrolling translates it downward inside the
 *    crop at a fraction of the scroll speed, clamped to the top
 *    slice (half the overflow) so no gap opens at the bottom. The
 *    opener always sits at the top of the page, so scrollY drives it
 *    directly. Skipped for reduced motion.
 */
function bindParallax(scope) {
  const media = scope.querySelector('.project-hero-open__media');
  if (!media || media.dataset.parallaxBound || prefersReduced.matches) return;
  const img = media.querySelector('img');
  if (!img) return;
  media.dataset.parallaxBound = 'true';
  let last = -1;
  const loop = () => {
    if (!media.isConnected) return; // stop after editor re-renders
    const overflow = img.clientHeight - media.clientHeight;
    const travel = overflow / 2; // top slice: the image sits centered at rest
    const shift = travel > 0 ? Math.min(window.scrollY * 0.22, travel) : 0;
    if (shift !== last) {
      last = shift;
      // Slight zoom that grows with the drift (up to +5% at full travel).
      const scale = 1 + 0.05 * (travel > 0 ? shift / travel : 0);
      img.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${scale.toFixed(4)})`;
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

/**
 * 4. Nav over the project opener — the hero tucks under the sticky
 *    header (negative margin = --header-height, measured here) and
 *    html.is-over-hero keeps the header transparent with the white
 *    wordmark (critical.css) until the hero's bottom edge passes the
 *    nav. project-hero.liquid sets the class inline on load; this
 *    owns it from then on.
 */
function bindHeroOverlay(scope) {
  const hero = scope.querySelector('.project-hero-open');
  const headerSection = document.querySelector('.shopify-section:has(> header)');
  if (!hero || !headerSection || hero.dataset.overlayBound) return;
  hero.dataset.overlayBound = 'true';
  const root = document.documentElement;
  const update = () => {
    if (!hero.isConnected) {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      return;
    }
    const headerHeight = headerSection.offsetHeight;
    root.style.setProperty('--header-height', `${headerHeight}px`);
    root.classList.toggle('is-over-hero', hero.getBoundingClientRect().bottom > headerHeight);
  };
  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
}

bindHero(document);
bindHeroOverlay(document);
bindParallax(document);
autoRevealImages(document);
document.querySelectorAll('.reveal').forEach((el) => revealObserver.observe(el));

/**
 * Theme editor compatibility: every setting tweak re-renders the
 * section via the Section Rendering API, replacing DOM this script
 * already processed — the fresh .reveal elements would sit at
 * opacity 0 until Save reloads the preview ("section goes blank while
 * editing"). Reveal them immediately (WYSIWYG beats a replayed
 * animation) and re-bind hero behaviors. The event only fires inside
 * the theme editor.
 */
document.addEventListener('shopify:section:load', (event) => {
  autoRevealImages(event.target);
  event.target.querySelectorAll('.reveal').forEach((el) => el.classList.add('reveal--visible'));
  bindHero(event.target);
  bindHeroOverlay(event.target);
  bindParallax(event.target);
});
